import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Shield,
  Ban,
  CheckCircle2,
  XCircle,
  Loader2,
  DollarSign,
  Heart,
  FileText,
  Clock,
  AlertCircle,
} from 'lucide-react';
import apiClient from '../services/api';
import { useAuth } from '../context/AuthContext';

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get(`/admin/users/${userId}/details`);
      setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view this user.');
      } else if (err.response?.status === 404) {
        setError('User not found.');
      } else {
        setError('Failed to load user details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, reason = '') => {
    setActionLoading(action);
    try {
      await apiClient.post(`/admin/users/${userId}/action`, { action, reason });
      await fetchUserDetails();
      setShowConfirmDialog(false);
    } catch (err) {
      console.error('Action failed:', err);
      setError(err.response?.data?.detail || 'Action failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmAction = (action) => {
    setPendingAction(action);
    setShowConfirmDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-red-400">
                <AlertCircle className="w-6 h-6" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
        </Button>

        {/* User Header */}
        <Card className="bg-card/50 border-border mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <img
                src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=10b981&color=fff&size=128`}
                alt={user?.name}
                className="w-24 h-24 rounded-full object-cover border-2 border-emerald-500/30"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-foreground">{user?.name}</h1>
                  <Badge variant={user?.suspended ? 'destructive' : 'default'}>
                    {user?.suspended ? 'Suspended' : user?.role || 'User'}
                  </Badge>
                  {user?.verified && (
                    <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> {user?.email}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Joined {new Date(user?.created_at).toLocaleDateString()}
                  </div>
                  {user?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> {user?.phone}
                    </div>
                  )}
                  {user?.country && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> {user?.country}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {user?.suspended ? (
                  <Button
                    variant="outline"
                    className="border-emerald-500/50 text-emerald-400"
                    onClick={() => handleAction('unsuspend')}
                    disabled={actionLoading === 'unsuspend'}
                  >
                    {actionLoading === 'unsuspend' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Unsuspend
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="border-red-500/50 text-red-400"
                    onClick={() => confirmAction('suspend')}
                    disabled={actionLoading === 'suspend'}
                  >
                    {actionLoading === 'suspend' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
                    Suspend
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">${user?.total_donated?.toLocaleString() || 0}</p>
              <p className="text-sm text-muted-foreground">Total Donated</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4 text-center">
              <Heart className="w-8 h-8 text-pink-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{user?.donations_count || 0}</p>
              <p className="text-sm text-muted-foreground">Donations Made</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{user?.campaigns_count || 0}</p>
              <p className="text-sm text-muted-foreground">Campaigns Created</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{user?.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</p>
              <p className="text-sm text-muted-foreground">Last Login</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for detailed info */}
        <Tabs defaultValue="campaigns" className="space-y-4">
          <TabsList className="bg-card/50">
            <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
            <TabsTrigger value="donations">Donations</TabsTrigger>
            <TabsTrigger value="activity">Activity Log</TabsTrigger>
          </TabsList>

          <TabsContent value="campaigns">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-foreground">User's Campaigns</CardTitle>
              </CardHeader>
              <CardContent>
                {user?.campaigns?.length > 0 ? (
                  <div className="space-y-3">
                    {user.campaigns.map((campaign) => (
                      <div key={campaign.id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                        <div>
                          <p className="font-medium text-foreground">{campaign.title}</p>
                          <p className="text-sm text-muted-foreground">
                            ${campaign.raised?.toLocaleString()} raised of ${campaign.goal?.toLocaleString()}
                          </p>
                        </div>
                        <Badge>{campaign.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No campaigns created</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="donations">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Donation History</CardTitle>
              </CardHeader>
              <CardContent>
                {user?.donations?.length > 0 ? (
                  <div className="space-y-3">
                    {user.donations.map((donation) => (
                      <div key={donation.id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                        <div>
                          <p className="font-medium text-foreground">{donation.campaign_title || 'Campaign'}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(donation.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="text-emerald-400 font-medium">${donation.amount?.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No donations made</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                {user?.activity_log?.length > 0 ? (
                  <div className="space-y-3">
                    {user.activity_log.map((activity, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-background/50 rounded-lg">
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                        <div>
                          <p className="text-foreground">{activity.action}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No activity recorded</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirm Action Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogDescription>
              Are you sure you want to {pendingAction} this user? This action can be reversed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleAction(pendingAction, `${pendingAction} by admin`)}
              disabled={actionLoading === pendingAction}
            >
              {actionLoading === pendingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminUserDetailPage;
